import { useState, useEffect } from 'react';
import { api, User } from '../../api';
import { getErrorMessage } from '../../../../shared/src';
import { StatusBadge, RoleSelector, RoleValue } from '../ui';
import { InviteUserModal } from '../modals';

interface UsersTabProps {
  authMethod: 'sso' | 'password';
  currentUserId?: string;
}

export function UsersTab({ authMethod, currentUserId }: UsersTabProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await api.listUsers();
      setUsers(data);
      setError('');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: RoleValue) => {
    setUpdatingUserId(userId);
    try {
      const result = await api.updateUserRole(userId, newRole);
      setUsers(users.map((u) => (u.id === userId ? result.user : u)));
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to update role: ${getErrorMessage(err)}` });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleInviteSuccess = (newUser: User) => {
    setUsers([...users, newUser]);
    setShowInviteModal(false);
  };

  const handleResendInvitation = async (userId: string) => {
    if (!window.confirm('Resend invitation email to this user?')) return;

    setActionInProgress(`resend-${userId}`);
    try {
      await api.resendInvitation(userId);
      setActionMessage({ type: 'success', text: 'Invitation email sent successfully' });
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to resend invitation: ${getErrorMessage(err)}` });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Remove ${userEmail} from this organization? This action cannot be undone.`)) return;

    setActionInProgress(`delete-${userId}`);
    try {
      await api.deleteUser(userId);
      setUsers(users.filter((u) => u.id !== userId));
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to remove user: ${getErrorMessage(err)}` });
    } finally {
      setActionInProgress(null);
    }
  };

  const canInviteUsers = authMethod === 'password';
  const canDeleteUsers = authMethod === 'password';

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="error-message">{error}</div>
        <button className="btn-primary" onClick={loadUsers}>Retry</button>
      </div>
    );
  }

  return (
    <div className="tab-content">
      {canInviteUsers && (
        <div className="tab-header">
          <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
            Invite User
          </button>
        </div>
      )}

      {actionMessage && (
        <div className={actionMessage.type === 'success' ? 'success-message' : 'error-message'}>
          {actionMessage.text}
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Last Login</th>
            {(canInviteUsers || canDeleteUsers) && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const isCurrentUser = user.id === currentUserId;
            const isProcessing = updatingUserId === user.id || actionInProgress?.includes(user.id);

            return (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>
                  {user.first_name} {user.last_name}
                  {isCurrentUser && <span className="you-badge">You</span>}
                </td>
                <td>
                  <RoleSelector
                    currentRole={user.role}
                    isUpdating={updatingUserId === user.id}
                    onRoleChange={(newRole) => handleRoleChange(user.id, newRole)}
                    disabled={isCurrentUser}
                  />
                </td>
                <td><StatusBadge status={user.status} /></td>
                <td>{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}</td>
                {(canInviteUsers || canDeleteUsers) && (
                  <td className="actions-cell">
                    {isProcessing ? (
                      <span className="processing">Processing...</span>
                    ) : (
                      <div className="action-buttons">
                        {canInviteUsers && user.status === 'invited' && (
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => handleResendInvitation(user.id)}
                          >
                            Resend Invite
                          </button>
                        )}
                        {canDeleteUsers && !isCurrentUser && (
                          <button
                            className="btn-small btn-danger"
                            onClick={() => handleDeleteUser(user.id, user.email)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          {users.length === 0 && (
            <tr>
              <td colSpan={canInviteUsers || canDeleteUsers ? 6 : 5} className="empty-state">
                {canInviteUsers
                  ? 'No users yet. Click "Invite User" to add the first user.'
                  : 'No users yet. Users will appear here after their first SSO login.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}
    </div>
  );
}
